// ** react imports
import React, { useState } from 'react';

// ** toaster imports
import { toast } from 'react-toastify';

// ** react redux imports
import { useDeleteMobileAppVersion } from '../../../hooks/apis/useMobileVersion';

// ** components
import ModalBasic from '../../../components/ModalBasic';
import Loading from '../../../components/common/Loading';
import MobileAppVersionTableItem from './MobileAppVersionTableItem';

const MobileAppVersionTable = props => {
  // props
  const { data, isLoading, currentSortBy, onSort, refetch } = props;

  // states
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [itemToDelete, setItemToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // hooks
  const { deleteMobileAppVersion } = useDeleteMobileAppVersion();

  // handlers
  const handleOpenDeleteModal = item => {
    setItemToDelete(item);
    setDeleteModalOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!itemToDelete) return;
    setIsDeleting(true);
    try {
      const response = await deleteMobileAppVersion(itemToDelete._id);
      if (response.data.success) {
        setDeleteModalOpen(false);
        refetch();
      }
    } catch (error) {
      toast.error(
        'An error occurred while deleting the mobile application version'
      );
    } finally {
      setIsDeleting(false);
    }
  };

  const SortIcon = ({ field }) => {
    if (currentSortBy !== field) {
      return (
        <svg
          className="w-3 h-3 ml-1 opacity-50"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }

    return currentSortBy === field && currentSortBy === 'asc' ? (
      <svg
        className="w-3 h-3 ml-1 text-violet-600 dark:text-violet-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 15l7-7 7 7"
        />
      </svg>
    ) : (
      <svg
        className="w-3 h-3 ml-1 text-violet-600 dark:text-violet-400"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    );
  };

  const handleSortClick = field => {
    if (onSort) {
      onSort(field);
    }
  };

  return (
    <div className="bg-white h-[calc(70vh)] dark:bg-gray-800 shadow-xs rounded-xl relative overflow-y-auto">
      {isLoading && (
        <div className="absolute inset-0 bg-white/50 dark:bg-gray-800/50 backdrop-blur-sm z-40 flex items-center justify-center rounded-xl">
          <div className="flex items-center gap-3 text-gray-600 dark:text-gray-300">
            <Loading />
          </div>
        </div>
      )}

      <div>
        <header className="px-4 sm:px-5 py-4 bg-white dark:bg-gray-800 sticky top-0 z-10 border-b border-gray-100 dark:border-gray-700/60 flex flex-col sm:flex-row justify-between items-start sm:items-center gap-3">
          <div>
            <h2 className="font-semibold text-gray-800 dark:text-gray-100">
              All Versions{' '}
              <span className="text-gray-400 dark:text-gray-500 font-medium">
                {data?.total || 0}
              </span>
            </h2>
          </div>
        </header>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-100 dark:divide-gray-700/60">
            <thead className="sticky top-0 z-10 text-xs font-semibold uppercase text-gray-500 dark:text-gray-300 bg-gray-50 dark:bg-gray-900/20 border-t border-b border-gray-100 dark:border-gray-700/60">
              <tr>
                <th
                  scope="col"
                  className="px-3 sm:px-4 py-3 min-w-[120px] max-w-[150px]"
                >
                  <button
                    className="font-semibold text-left whitespace-nowrap uppercase flex items-center hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    onClick={() => handleSortClick('versionId')}
                    aria-label="Sort by version ID"
                  >
                    Version ID
                    <SortIcon field="versionId" />
                  </button>
                </th>
                <th
                  scope="col"
                  className="px-3 sm:px-4 py-3 min-w-[100px] max-w-[120px]"
                >
                  <button
                    className="font-semibold text-left whitespace-nowrap uppercase flex items-center hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    onClick={() => handleSortClick('platform')}
                    aria-label="Sort by platform"
                  >
                    Platform
                    <SortIcon field="platform" />
                  </button>
                </th>
                <th
                  scope="col"
                  className="px-3 sm:px-4 py-3 min-w-[100px] max-w-[120px]"
                >
                  <button
                    className="font-semibold text-left whitespace-nowrap uppercase flex items-center hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    onClick={() => handleSortClick('currentVersion')}
                    aria-label="Sort by current version"
                  >
                    Current Version
                    <SortIcon field="currentVersion" />
                  </button>
                </th>
                <th
                  scope="col"
                  className="px-3 sm:px-4 py-3 min-w-[100px] max-w-[120px] text-center"
                >
                  <button
                    className="font-semibold text-center whitespace-nowrap uppercase flex items-center justify-center hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    onClick={() => handleSortClick('isActive')}
                    aria-label="Sort by active status"
                  >
                    Is Active
                    <SortIcon field="isActive" />
                  </button>
                </th>
                <th
                  scope="col"
                  className="px-3 sm:px-4 py-3 min-w-[150px] max-w-[200px]"
                >
                  <div className="font-semibold text-left whitespace-nowrap">
                    Release Notes
                  </div>
                </th>
                <th
                  scope="col"
                  className="px-3 sm:px-4 py-3 min-w-[120px] max-w-[150px]"
                >
                  <button
                    className="font-semibold text-left whitespace-nowrap uppercase flex items-center hover:text-violet-600 dark:hover:text-violet-400 transition-colors"
                    onClick={() => handleSortClick('expiryDate')}
                    aria-label="Sort by expiry date"
                  >
                    Expiry Date
                    <SortIcon field="expiryDate" />
                  </button>
                </th>
                <th
                  scope="col"
                  className="px-3 sm:px-4 py-3 min-w-[80px] max-w-[100px] text-center"
                >
                  <div className="font-semibold text-center">Actions</div>
                </th>
              </tr>
            </thead>
            <tbody className="text-sm divide-y divide-gray-100 dark:divide-gray-700/60">
              {data?.data && data.data.length > 0 ? (
                data.data.map(appVersion => {
                  return (
                    <MobileAppVersionTableItem
                      data={appVersion}
                      key={appVersion._id}
                      viewMode="table"
                      onOpenDeleteModal={handleOpenDeleteModal}
                    />
                  );
                })
              ) : (
                <tr className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                  <td colSpan="7" className="px-4 py-12 text-center">
                    <div className="flex flex-col items-center justify-center">
                      <svg
                        className="w-12 h-12 text-gray-400 dark:text-gray-500 mb-4"
                        xmlns="http://www.w3.org/2000/svg"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                      >
                        <path d="M18 2C18.5523 2 19 2.44772 19 3V11H17V4H7V20H11V22H6C5.44772 22 5 21.5523 5 21V3C5 2.44772 5.44772 2 6 2H18ZM15 12C17.2091 12 19 13.7909 19 16C19 16.7419 18.798 17.4366 18.446 18.0322L20.6569 20.2426L19.2426 21.6569L17.0313 19.4466C16.4359 19.7982 15.7415 20 15 20C12.7909 20 11 18.2091 11 16C11 13.7909 12.7909 12 15 12ZM15 14C13.8954 14 13 14.8954 13 16C13 17.1046 13.8954 18 15 18C16.1046 18 17 17.1046 17 16C17 14.8954 16.1046 14 15 14Z"></path>
                      </svg>
                      <h3 className="text-lg font-medium text-gray-900 dark:text-gray-100 mb-2">
                        No Application Version found
                      </h3>
                      <p className="text-gray-500 dark:text-gray-400 text-sm">
                        Try adjusting your filters or search criteria to find
                        what you&apos;re looking for.
                      </p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      <ModalBasic
        id={`delete-modal-${itemToDelete?._id}`}
        modalOpen={deleteModalOpen}
        setModalOpen={setDeleteModalOpen}
        title="Delete Mobile Application Version"
      >
        <div className="px-5 pt-4 pb-1">
          <div className="text-sm mb-4">
            <p className="text-gray-600 dark:text-gray-300">
              Are you sure you want to delete the mobile application version{' '}
              <strong>{itemToDelete?.versionId}</strong>?
            </p>
            <p className="text-gray-600 dark:text-gray-300 mt-2">
              This action cannot be undone. The version will be permanently
              removed from the system.
            </p>
          </div>
        </div>
        <div className="px-5 py-4 border-t border-gray-200 dark:border-gray-700/60 flex justify-end space-x-3">
          <button
            className="btn px-4 py-2 rounded-lg border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-700/50 transition-colors"
            onClick={() => setDeleteModalOpen(false)}
            disabled={isDeleting}
          >
            Cancel
          </button>
          <button
            className="btn px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white transition-colors flex items-center"
            onClick={handleDeleteConfirm}
            disabled={isDeleting}
          >
            {isDeleting ? (
              <>
                <svg
                  className="animate-spin -ml-1 mr-2 h-4 w-4 text-white"
                  xmlns="http://www.w3.org/2000/svg"
                  fill="none"
                  viewBox="0 0 24 24"
                >
                  <circle
                    className="opacity-25"
                    cx="12"
                    cy="12"
                    r="10"
                    stroke="currentColor"
                    strokeWidth="4"
                  ></circle>
                  <path
                    className="opacity-75"
                    fill="currentColor"
                    d="M4 12a8 8 0 018-8V0C5.373 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                  ></path>
                </svg>
                Deleting...
              </>
            ) : (
              'Delete'
            )}
          </button>
        </div>
      </ModalBasic>
    </div>
  );
};

export default MobileAppVersionTable;
